import {
    AutomationType,
    DisabledAutomation,
    DisabledIntegration,
    DisabledOrLimitedSubscriptionFeature,
    IntegrationCapability,
    SubscriptionFeaturesAndCapabilities,
    SubscriptionFeatureType,
    SubscriptionStatus,
} from '../../types';
import * as React from 'react';
import Alert from 'antd/lib/alert';
import Tooltip from 'antd/lib/tooltip';
import { WarningOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { getLowestPlanWithFeature, isSubscriptionPlanPaidCustomer } from '../../helpers';
import Text from 'antd/lib/typography/Text';
import { displaySubscriptionStatus } from '../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectOrg,
    selectShouldDisableFeature,
    selectShouldDisableIntegrationCapability,
    selectShouldDisableAutomation,
    selectIsTrialStarted,
    selectIsTrialEnded,
    selectTrialType,
    selectCurrentSubscriptionStatus,
    selectFeaturesAndCapabilitiesPerSubscriptionStatus,
} from '../../selectors/selectors';
import { presentUserflowTrialActivation } from '../TrialBanner';
import TrackedButton from '../../helpers/TrackedButton';
import * as Amplitude from '../../utils/analytics';

export const useRestrictedFeatureIsDisabled = (
    feature: SubscriptionFeatureType,
    automationType?: AutomationType,
    integrationCapability?: IntegrationCapability,
) => {
    return useSelector((state: RootState) => {
        const orgId = selectOrg(state)!.id;

        if (feature) {
            return selectShouldDisableFeature(state, orgId, feature);
        } else if (integrationCapability) {
            return selectShouldDisableIntegrationCapability(state, orgId, integrationCapability);
        } else if (automationType) {
            return selectShouldDisableAutomation(state, orgId, automationType);
        } else {
            return false;
        }
    });
};

export const useFeaturePlanRestrictionTooltipContent = (feature: SubscriptionFeatureType): React.ReactNode => {
    const orgId = useSelector((state: RootState) => selectOrg(state).id);
    const isTrialStarted = useSelector(selectIsTrialStarted);
    const isTrialEnded = useSelector(selectIsTrialEnded);
    const trialType = useSelector(selectTrialType);
    const subscriptionStatus = useSelector(selectCurrentSubscriptionStatus);
    const featuresAndCapabilitiesPerSubscriptionStatus = useSelector(
        selectFeaturesAndCapabilitiesPerSubscriptionStatus,
    );
    const featureCapabilityOrAutomationDisabledData =
        featuresAndCapabilitiesPerSubscriptionStatus[subscriptionStatus]?.disabledOrLimitedFeaturesMap[feature];

    return tooltipContentForFeatureOrCapabilityUsingTrialType({
        orgId,
        feature,
        featureCapabilityOrAutomationDisabledData,
        isTrialStarted,
        isTrialEnded,
        trialType,
        subscriptionStatus,
        featuresAndCapabilitiesPerSubscriptionStatus,
    });
};

export const tooltipContentForFeatureOrCapabilityUsingTrialType = (params: {
    orgId: string;
    feature?: SubscriptionFeatureType;
    integrationCapability?: IntegrationCapability;
    automationType?: AutomationType;
    featureCapabilityOrAutomationDisabledData:
        | DisabledOrLimitedSubscriptionFeature
        | DisabledIntegration
        | DisabledAutomation;
    isTrialStarted: boolean;
    isTrialEnded: boolean;
    trialType: SubscriptionStatus;
    subscriptionStatus: SubscriptionStatus;
    featuresAndCapabilitiesPerSubscriptionStatus: Record<SubscriptionStatus, SubscriptionFeaturesAndCapabilities>;
}): React.ReactNode => {
    const {
        orgId,
        feature,
        integrationCapability,
        automationType,
        featureCapabilityOrAutomationDisabledData,
        isTrialStarted,
        isTrialEnded,
        trialType,
        subscriptionStatus,
        featuresAndCapabilitiesPerSubscriptionStatus,
    } = params;

    if (!feature && !integrationCapability && !automationType) {
        return null;
    }

    const isCustomer = isSubscriptionPlanPaidCustomer(subscriptionStatus);
    const isTrialActive = !isTrialEnded && !isCustomer && isTrialStarted;

    const contentNode = getTooltipContentForFeatureOrCapability({
        feature,
        integrationCapability,
        automationType,
        featuresAndCapabilitiesPerSubscriptionStatus,
        featureCapabilityOrAutomationDisabledData,
        isTrialActive,
    });

    // expired trial or paying customer
    if (isTrialEnded || isCustomer) {
        let trialOverText = 'to upgrade your organization’s plan';

        // special case for add apps feature

        if (
            (featureCapabilityOrAutomationDisabledData as DisabledOrLimitedSubscriptionFeature)?.feature ===
                SubscriptionFeatureType.AddApps &&
            !(featureCapabilityOrAutomationDisabledData as DisabledOrLimitedSubscriptionFeature)?.limit
        ) {
            trialOverText = 'to add another app to your plan';
        }

        return (
            <>
                {contentNode && (
                    <>
                        <Text>{contentNode}</Text>
                        <br />
                        <br />
                    </>
                )}
                <Text>
                    <Link href={`#hs-chat-open`}>Get in touch</Link> {trialOverText}.
                </Text>
            </>
        );
    }

    // trial is available
    if (!isTrialStarted && !isCustomer) {
        return (
            <>
                {contentNode && (
                    <>
                        <Text>{contentNode}</Text>
                        <br />
                        <br />
                    </>
                )}
                <Text>
                    <TrackedButton
                        orgId={orgId}
                        type="link"
                        style={{ margin: 0, padding: 0, height: 'unset' }}
                        eventName={Amplitude.Event.ClickActivateOrgSubscriptionTrial}
                        extraProperties={{
                            placement: 'tooltip',
                        }}
                        onClick={presentUserflowTrialActivation}
                    >
                        Click here
                    </TrackedButton>{' '}
                    to activate your
                    <br />
                    <strong>free 3 week trial</strong> including all Runway features.
                </Text>
            </>
        );
    }

    // else indicate they are on trial
    return (
        <>
            <Text>
                {contentNode}
                {` `}
                You are currently on a free trial of the <strong>
                    {displaySubscriptionStatus(trialType, false)}
                </strong>{' '}
                plan.
            </Text>
        </>
    );
};

export const getTooltipContentForFeatureOrCapability: (params: {
    feature?: SubscriptionFeatureType;
    integrationCapability?: IntegrationCapability;
    automationType?: AutomationType;
    featuresAndCapabilitiesPerSubscriptionStatus: Record<SubscriptionStatus, SubscriptionFeaturesAndCapabilities>;
    featureCapabilityOrAutomationDisabledData?:
        | DisabledOrLimitedSubscriptionFeature
        | DisabledIntegration
        | DisabledAutomation;
    isTrialActive: boolean;
}) => React.ReactNode = ({
    feature,
    integrationCapability,
    automationType,
    featuresAndCapabilitiesPerSubscriptionStatus,
    featureCapabilityOrAutomationDisabledData,
    isTrialActive,
}) => {
    if (!feature && !integrationCapability && !automationType) {
        return null;
    }

    let contentNode: React.ReactNode = null;

    const lowestPlanWithFeature = getLowestPlanWithFeature({
        feature,
        automationType,
        integrationCapability,
        featuresAndCapabilitiesPerSubscriptionStatus,
    });

    if (feature) {
        const limit =
            featureCapabilityOrAutomationDisabledData ?
                (featureCapabilityOrAutomationDisabledData as DisabledOrLimitedSubscriptionFeature).limit
            :   2; // TODO: figure out how to get this dynamically depending on the feature type when disabled data not available

        const defaultRestrictedFeatureContent = (
            <Text>
                This feature is available on the{' '}
                <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and above.
            </Text>
        );

        switch (feature) {
            // capacity-controlled features
            case SubscriptionFeatureType.FeatureCLI:
                contentNode = (
                    <Text>
                        Adding more than {limit} checklist items per release step is only available on the{' '}
                        <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and
                        above.
                    </Text>
                );
                break;
            case SubscriptionFeatureType.WriteUsers:
                contentNode = (
                    <Text>
                        Adding more than {limit} read-write users is only available on the{' '}
                        <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and
                        above.
                    </Text>
                );
                break;
            case SubscriptionFeatureType.AddApps:
                contentNode =
                    limit ?
                        <Text>
                            Adding or managing more than {limit} apps is only available on the{' '}
                            <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan
                            and above.
                        </Text>
                    :   null; // where no limit exists but feature is disabled, just show "contact us" text
                break;
            case SubscriptionFeatureType.ReleaseHistory:
            case SubscriptionFeatureType.InsightsHistory:
                // TODO: add support for the other limited features
                contentNode = (
                    <Text>
                        This feature is limited on the{' '}
                        <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and
                        above.
                    </Text>
                );
                break;
            default:
                contentNode = defaultRestrictedFeatureContent;
                break;
        }
    }

    if (integrationCapability) {
        contentNode = (
            <Text>
                This integration is available on the{' '}
                <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and above.
            </Text>
        );
    }

    if (automationType) {
        contentNode = (
            <Text>
                This automation is available on the{' '}
                <strong>{displaySubscriptionStatus(lowestPlanWithFeature!, !isTrialActive)}</strong> plan and above.
            </Text>
        );
    }

    return contentNode;
};

export const AUTOMATION_PLAN_EXECUTION_BLOCKED_MESSAGE =
    'This automation is not available on your current plan and will not execute.';

export const AutomationPlanExecutionBlockedAlert: React.FC<{
    visible: boolean;
    style?: React.CSSProperties;
}> = ({ visible, style }) => {
    if (!visible) {
        return null;
    }
    return (
        <Alert
            type="warning"
            showIcon
            closable={false}
            message={AUTOMATION_PLAN_EXECUTION_BLOCKED_MESSAGE}
            style={{ marginBottom: 16, ...style }}
        />
    );
};

export const AutomationPlanExecutionTooltipIcon: React.FC<{ visible: boolean }> = ({ visible }) => {
    if (!visible) {
        return null;
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
            <Tooltip
                title={AUTOMATION_PLAN_EXECUTION_BLOCKED_MESSAGE}
                placement="topLeft"
                overlayInnerStyle={{ color: antGrey.grey9, maxWidth: 360 }}
                color={runwayColors.white}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 0,
                    }}
                >
                    <WarningOutlined
                        style={{
                            color: runwayColors.statusYellow,
                            fontSize: 16,
                            lineHeight: 1,
                            cursor: 'help',
                        }}
                        aria-label="Automation will not run on current plan"
                    />
                </span>
            </Tooltip>
        </span>
    );
};
